import React, { useEffect, useRef } from 'react';

interface NavigationItem {
  key: string;
  label: string | React.ReactNode;
  icon: React.ReactNode;
  loading?: boolean;
}

interface NavigationBarProps {
  items: NavigationItem[];
  activeItem: string;
  onNavigate?: (key: string) => void;
  containerRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Панель навигации внутри хедера.
 * Отображает кнопки навигации с подсветкой активного пункта.
 */
export const NavigationBar: React.FC<NavigationBarProps> = ({
  items,
  activeItem,
  onNavigate,
  containerRef,
}) => {
  const activeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Скроллим только сам nav по горизонтали (для узких экранов), а не
    // scrollIntoView — тот бы задел вертикальный скролл всей страницы
    // через sticky-хедер и мог увести контент выше вьюпорта (см. баннер эмуляции).
    const button = activeButtonRef.current;
    const container = containerRef?.current;
    if (!button || !container) return;

    const buttonRect = button.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    if (buttonRect.left < containerRect.left) {
      container.scrollLeft -= containerRect.left - buttonRect.left;
    } else if (buttonRect.right > containerRect.right) {
      container.scrollLeft += buttonRect.right - containerRect.right;
    }
  }, [activeItem, containerRef]);

  return (
    <nav
      ref={containerRef}
      className="navigation"
      data-tour-id="main-navigation"
      aria-label="Основная навигация"
    >
      <div className="navigation-track">
        {items.map((item) =>
          item.loading ? (
            <span key={item.key} className="nav-button nav-button--skeleton" aria-hidden>
              <span className="nav-skeleton-bar" />
            </span>
          ) : (
            <button
              ref={activeItem === item.key ? activeButtonRef : undefined}
              key={item.key}
              type="button"
              onClick={() => onNavigate?.(item.key)}
              className={`nav-button ${activeItem === item.key ? 'active' : ''}`}
              title={typeof item.label === 'string' ? item.label : undefined}
              aria-current={activeItem === item.key ? 'page' : undefined}
            >
              {item.icon}
              <span className="nav-button-label">{item.label}</span>
            </button>
          )
        )}
      </div>
    </nav>
  );
};
